import type { TranslationTag } from '@ps/web-core-intl';
import { canUseDOM } from '@ps/web-core-utils';
import type { GlobalFieldReference, ReferAFriend } from '@ps/web-martech-cms-types';
import { StarsButton, Translation, useIsMounted } from '@ps/web-shared-components';
import classNames from 'clsx';
import { identity, pickBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import URL from 'url-parse';

import { CMSReference } from '@swf/cms';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';

import {
  TXTCLI_Copy_link,
  TXTCLI_Failed_to_copy_linkX,
  TXTCLI_Link_copiedX,
  TXTCLI_Login,
  TXTCLI_Your_referral_link
} from './CMSReferAFriend.messages.json';
import styles from './CMSReferAFriend.scss';
import SocialShareButtons from './SocialShareButtons/SocialShareButtons';
import ShareLinkButton from './SocialShareButtons/buttons/ShareLinkButton';

const getBackgroundImageStyle = (isMobile: boolean, url?: string) =>
  url && !isMobile ? { backgroundImage: `url(${url})`, backgroundSize: 'cover', padding: 20 } : {};

const CMSReferAFriend: React.FC<ReferAFriend> = ({
  background_color: backgroundColor,
  background_image: backgroundImage,
  base_url: baseUrl,
  button_alignment: buttonAlignment,
  logged_in_body: loggedInBody = [],
  logged_out_body: loggedOutBody = [],
  text_color: color,
  social_icons_color: socialIconsColor,
  icons_border_color: iconsBorderColor
}) => {
  const [copySuccess, setCopySuccess] = useState<TranslationTag>();
  const userWebId = useSelector(select.session.getWebId);
  const isMobile = useSelector(select.device.isMobile);
  const { modal } = useDispatch<Dispatch>();
  const openLoginModal = useCallback(() => modal.enqueue({ path: '/auth/login' }), [modal]);
  const [webId, setWebId] = useState('');
  const isMounted = useIsMounted();

  useEffect(() => {
    isMounted() && userWebId && setWebId(userWebId);
  }, [isMounted, userWebId]);

  const nativeShare = isMobile && canUseDOM && typeof navigator.share !== 'undefined';
  let body;
  let rafLink = '';

  if (webId) {
    rafLink = new URL(baseUrl).set('query', { source: `RAF_${webId}` }).toString();
    body = loggedInBody.length ? loggedInBody : loggedOutBody;
  } else {
    body = loggedOutBody.length ? loggedOutBody : loggedInBody;
  }

  useEffect(() => {
    if (!webId) {
      setCopySuccess(undefined);
    }
  }, [webId]);

  const copyToClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(rafLink);
      setCopySuccess(TXTCLI_Link_copiedX);
    } catch {
      setCopySuccess(TXTCLI_Failed_to_copy_linkX);
    }
  };

  return (
    <section style={getBackgroundImageStyle(isMobile, backgroundImage?.url)}>
      <div className={styles.widget} style={pickBy({ backgroundColor, color }, identity)}>
        {body?.map((item, index) => (
          <CMSReference key={index} {...(item as GlobalFieldReference)} />
        ))}
        <div className={classNames(styles.linkContainer, rafLink && styles.topAlignment)}>
          {rafLink && (
            <p className={styles.label}>
              <Translation {...TXTCLI_Your_referral_link} />
            </p>
          )}
          <div className={classNames(styles.linkRow, buttonAlignment && styles[buttonAlignment])}>
            {rafLink && <p className={styles.generatedLink}>{rafLink}</p>}
            {nativeShare && rafLink ? (
              <ShareLinkButton url={rafLink} />
            ) : (
              <StarsButton
                className={styles.generateLinkBtn}
                onClick={rafLink ? copyToClipBoard : openLoginModal}
                translation={rafLink ? TXTCLI_Copy_link : TXTCLI_Login}
              />
            )}
          </div>
          {copySuccess && (
            <p className={styles.copySuccess}>
              <Translation {...copySuccess} />
            </p>
          )}
          {rafLink && !nativeShare && (
            <SocialShareButtons
              url={rafLink}
              socialIconsColor={socialIconsColor}
              iconsBorderColor={iconsBorderColor}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default CMSReferAFriend;
